import React, { useMemo } from 'react';
import api from '~/services/api';
import * as Sentry from '@sentry/react';

import FormSectionCard from '~/componentes/FormSectionCard';
import InputText from '~/componentes/Inputs/InputText';
import InputDateTime from '~/componentes/Inputs/InputDateTime';
import InputTime from '~/componentes/Inputs/InputTime';
import InputDate from '~/componentes/Inputs/InputDate';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import InputTextarea from '~/componentes/Inputs/InputTextarea';
import InputCheckbox from '~/componentes/Inputs/InputCheckBox';
import InputRadio from '~/componentes/Inputs/InputRadio';
import InputSelect from '~/componentes/Inputs/InputSelect';
import IconLixeira from '~/assets/icones/IconLixeira.svg';
import ShowConfirmacao from '~/utils/ShowConfirmacao';
import ShowError from '~/utils/ShowError';
import { useSelector } from 'react-redux';
import { SLForm } from '~/pages/Sistema/componentes/SistemaList';
import sistemaListStore, {
  sistemaListShowModal,
} from '~/pages/Sistema/componentes/SistemaList/ThisMainComponent/thisStore';
import { pdf } from '@react-pdf/renderer';
import GmudPdfTemplatePDF from '~/componentes/TemplatePDF/Gmud';
import formatDateToSaoPauloTimezone from '~/utils/formatDateToSaoPauloTimezone';
import * as S from './styles';

const schema = z
  .object({
    //Dados do Solicitante
    idCliente: z.string().min(1, 'Cliente é obrigatório'),
    idSolicitante: z.string().min(1, 'Solicitante é obrigatório'),
    idModuloTotvs: z.string().min(1, 'Módulo TOTVS é obrigatório'),
    idVersaoSistemaTotvs: z.string().optional(),
    // solicitanteEmail: z.string().optional(),
    // area: z.string().min(1, 'Área é obrigatória'),
    dataSolicitacao: z
      .date({
        required_error: 'Data de solicitação é obrigatória',
      })
      .transform(val => val.toISOString()),

    //Responsáveis pela Mudança
    idCoordenador: z.string().min(1, 'Coordenador é obrigatório'),
    idExecutor: z.string().min(1, 'Executor é obrigatório'),
    dataHoraInicio: z
      .date({
        required_error: 'Data e hora início é obrigatória',
      })
      .transform(val => val.toISOString()),
    dataHoraFim: z
      .date({
        required_error: 'Data e hora fim é obrigatória',
      })
      .transform(val => val.toISOString()),
    status: z.string().min(1, 'Status é obrigatório'),
    motivoCancelamento: z.string().optional(),

    //Descrição das Mudanças
    titulo: z.string().min(1, 'Título é obrigatório'),
    descricaoMudanca: z.string().min(1, 'Descrição é obrigatória'),
    descricaoAtividade: z.string().min(1, 'Descrição é obrigatória'),
    linksTdnTotvs: z
      .array(
        z.object({
          url: z.string().url('URL deve ser válida').optional(),
        }),
      )
      .optional(),

    //Classificações
    atualizacaoDePatch: z.boolean().optional(),
    atualizacaoDeLib: z.boolean().optional(),
    atualizacaoDeStoredProcedure: z.boolean().optional(),
    atualizacaoDeSchemasTss: z.boolean().optional(),
    atualizacaoDeMashup: z.boolean().optional(),
    atualizacaoDeTss: z.boolean().optional(),
    atualizacaoDeUpddistr: z.boolean().optional(),
    compilaFonte: z.boolean().optional(),
    outros: z.boolean().optional(),
    githubHashCommit: z
      .string()
      .optional()
      .refine(
        value => {
          if (!value || value.trim() === '') return true; // Optional field
          return /^[a-fA-F0-9]{40}$/.test(value.trim());
        },
        {
          message:
            'Hash do commit deve ter exatamente 40 caracteres hexadecimais (ex: ffc3ad03eb1496f8a489b9c4873965e1a1540393)',
        },
      ),
    outrosClassificacao: z.string().optional(),

    // InputRadio (Categoria, Prioridade, Origem, Ambiente, Finalidade, Haverá Indisponibilidade ,Janela Acordada, Dependência de outra mudança)
    categoria: z.string().min(1, 'Categoria é obrigatória'),
    prioridade: z.string().min(1, 'Prioridade é obrigatória'),
    origem: z.string().min(1, 'Origem é obrigatória'),
    numeroTicket: z.string().optional(),
    ambiente: z.string().min(1, 'Ambiente é obrigatória'),
    finalidade: z.string().min(1, 'Finalidade é obrigatória'),
    janelaAcordada: z.string().min(1, 'Janela Acordada é obrigatória'),
    janelaAcordadaComQuem: z.string().optional(),
    dependeDeOutraMudanca: z.string().min(1, 'Informe se depende de outra mudança'),
    dependeDeOutraMudancaQual: z.string().optional(),
    indisponibilidade: z.string().min(1, 'Informe se haverá indisponibilidade'),
    tempoIndisponibilidade: z
      .number()
      // .min(1, 'Tempo de indisponibilidade é obrigatório')
      .optional(),

    // Plano de execução da mudança
    atividades: z.array(
      z.object({
        id: z.number().optional(),
        descricao: z.string().min(1, 'Descrição da atividade é obrigatória'),
        idResponsavel: z.string().min(1, 'Responsável é obrigatório'),
        dataHoraInicio: z
          .date({
            required_error: 'Data e hora início é obrigatório',
          })
          .transform(val => val.toISOString()),
        tempoPrevistoEmMinutos: z.number().min(1, 'Tempo previsto é obrigatório'),
      }),
    ),

    // Plano de validação da mudança
    validacao: z.array(
      z.object({
        id: z.number().optional(),
        descricao: z.string().min(1, 'Descrição da ação é obrigatória'),
        resultado: z.string().min(1, 'Resultado esperado é obrigatório'),
        passfail: z.string().optional(),
        comentario: z.string().optional(),
      }),
    ),

    // Plano de Rollback (para o caso de problemas)
    rollback: z.array(
      z.object({
        id: z.number().optional(),
        descricao: z.string().min(1, 'Descrição da ação é obrigatória'),
        idResponsavel: z.string().min(1, 'Responsável é obrigatório'),
        datahora: z
          .date({
            required_error: 'Data e hora é obrigatório',
          })
          .transform(val => val.toISOString()),
        comentario: z.string().optional(),
      }),
    ),

    // Plano de Escalonamento em Caso de Problemas
    escalonamento: z.array(
      z.object({
        id: z.number().optional(),
        idResponsavel: z.string().optional(),
        nome: z.string().min(1, 'Nome é obrigatório'),
        telefone: z.string(),
      }),
    ),
  })
  .superRefine((data, ctx) => {
    if (
      data.status === 'cancelada' &&
      (!data.motivoCancelamento || data.motivoCancelamento.trim() === '')
    ) {
      ctx.addIssue({
        path: ['motivoCancelamento'],
        code: z.ZodIssueCode.custom,
        message: 'Motivo do cancelamento é obrigatório.',
      });
    }
    if (data.outros && !data.outrosClassificacao) {
      ctx.addIssue({
        path: ['outrosClassificacao'],
        code: z.ZodIssueCode.custom,
        message: 'Especifique o outro tipo de classificação.',
      });
    }
    if (data.janelaAcordada === 'sim' && !data.janelaAcordadaComQuem) {
      ctx.addIssue({
        path: ['janelaAcordadaComQuem'],
        code: z.ZodIssueCode.custom,
        message: 'Informe com quem foi a janela acordada.',
      });
    }
    if (data.dependeDeOutraMudanca === 'sim' && !data.dependeDeOutraMudancaQual) {
      ctx.addIssue({
        path: ['dependeDeOutraMudancaQual'],
        code: z.ZodIssueCode.custom,
        message: 'Informe a qual mudança depende.',
      });
    }
    if (data.compilaFonte && (!data.githubHashCommit || data.githubHashCommit.trim() === '')) {
      ctx.addIssue({
        path: ['githubHashCommit'],
        code: z.ZodIssueCode.custom,
        message: 'O hash do commit do GitHub é obrigatório.',
      });
    }
    if (data.indisponibilidade === 'sim' && !data.tempoIndisponibilidade) {
      ctx.addIssue({
        path: ['tempoIndisponibilidade'],
        code: z.ZodIssueCode.custom,
        message: 'Informe o tempo de indisponibilidade.',
      });
    }
    if (data.origem === 'ticket' && (!data.numeroTicket || data.numeroTicket.trim() === '')) {
      ctx.addIssue({
        path: ['numeroTicket'],
        code: z.ZodIssueCode.custom,
        message: 'Número do ticket é obrigatório.',
      });
    }
    if (data.ambiente === 'producao' && !data.idVersaoSistemaTotvs) {
      ctx.addIssue({
        path: ['idVersaoSistemaTotvs'],
        code: z.ZodIssueCode.custom,
        message: 'Selecione a versão do sistema TOTVS.',
      });
    }
  });

function EditContent({
  mountedData,
  refetch,
  perfilAtual,
  analistas,
  clientesSolicitantes,
  coordenadores,
  clientes,
  modulosTotvs,
  versoesSistemaTotvs,
}) {
  const { perfil } = useSelector(state => state.auth);
  const { state, dispatch } = React.useContext(sistemaListStore);
  const { editShow } = state?.modal || {};

  const {
    register,
    control,
    formState: { errors },
    setError,
    setValue,
    watch,
    handleSubmit: handleFormSubmit,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      // Dados do Solicitante
      idCliente: String(mountedData?.idCliente || ''),
      idSolicitante: String(mountedData.idSolicitante || ''),
      idModuloTotvs: String(mountedData.idModuloTotvs || ''),
      idVersaoSistemaTotvs: String(mountedData.idVersaoSistemaTotvs || ''),
      // solicitanteEmail: mountedData.clienteSolicitanteGmud?.email || '',
      // area: mountedData.area || '',
      dataSolicitacao: mountedData.dataSolicitacao
        ? new Date(mountedData.dataSolicitacao)
        : new Date(),

      // Responsáveis pela Mudança
      idCoordenador: String(mountedData.idCoordenador || ''),
      idExecutor: String(mountedData.idExecutor || ''),
      dataHoraInicio: mountedData.dataHoraInicio
        ? new Date(mountedData.dataHoraInicio)
        : new Date(),
      dataHoraFim: mountedData.dataHoraFim ? new Date(mountedData.dataHoraFim) : new Date(),
      status: mountedData.status ?? 'pendente',
      motivoCancelamento: mountedData.motivoCancelamento || '',

      // Descrição das Mudanças
      titulo: mountedData.titulo || '',
      descricaoMudanca: mountedData.descricaoMudanca || '',
      descricaoAtividade: mountedData.descricaoAtividade || '',
      linksTdnTotvs: mountedData.linksTdnTotvs?.map(link => ({ url: link.url })) || [],

      // Classificações
      atualizacaoDePatch: mountedData.atualizacaoDePatch || false,
      atualizacaoDeLib: mountedData.atualizacaoDeLib || false,
      atualizacaoDeStoredProcedure: mountedData.atualizacaoDeStoredProcedure || false,
      atualizacaoDeSchemasTss: mountedData.atualizacaoDeSchemasTss || false,
      atualizacaoDeMashup: mountedData.atualizacaoDeMashup || false,
      atualizacaoDeTss: mountedData.atualizacaoDeTss || false,
      atualizacaoDeUpddistr: mountedData.atualizacaoDeUpddistr || false,
      compilaFonte: mountedData.compilaFonte || false,
      outros: mountedData.outros || false,
      githubHashCommit: mountedData.githubHashCommit || '',
      outrosClassificacao: mountedData.outraClassificacao || '',

      // InputRadio
      categoria: mountedData.categoria || '',
      prioridade: mountedData.prioridade || '',
      origem: mountedData.origem || '',
      numeroTicket: mountedData.numeroTicket || '',
      ambiente: mountedData.ambiente || '',
      finalidade: mountedData.finalidade || '',
      janelaAcordada: mountedData.janelaAcordadaComQuem ? 'sim' : 'nao',
      janelaAcordadaComQuem: mountedData.janelaAcordadaComQuem ?? undefined,
      dependeDeOutraMudanca: mountedData.dependeDeOutraMudancaQual ? 'sim' : 'nao',
      dependeDeOutraMudancaQual: mountedData.dependeDeOutraMudancaQual ?? undefined,
      indisponibilidade: mountedData.tempoIndisponibilidadeEmMinutos ? 'sim' : 'nao',
      tempoIndisponibilidade: mountedData.tempoIndisponibilidadeEmMinutos ?? undefined,

      // Plano de execução da mudança
      atividades:
        mountedData.execucao?.length > 0
          ? mountedData.execucao.map(execucao => ({
              descricao: execucao.descricao || '',
              idResponsavel: String(execucao.idResponsavel || ''),
              dataHoraInicio: execucao.dataHoraInicio
                ? new Date(execucao.dataHoraInicio)
                : new Date(),
              tempoPrevistoEmMinutos: execucao.tempoPrevistoEmMinutos || 0,
            }))
          : [],

      // Plano de validação da mudança
      validacao:
        mountedData.validacao?.length > 0
          ? mountedData.validacao.map(validacao => ({
              descricao: validacao.descricao || '',
              resultado: validacao.resultado || '',
              passfail:
                validacao.passfail === 1 ? 'pass' : validacao.passfail === 0 ? 'fail' : undefined,
              comentario: validacao.comentario ?? undefined,
            }))
          : [],

      // Plano de Rollback
      rollback:
        mountedData.rollback?.length > 0
          ? mountedData.rollback.map(rollback => ({
              descricao: rollback.descricao || '',
              idResponsavel: String(rollback.idResponsavel || ''),
              datahora: rollback.datahora ? new Date(rollback.datahora) : new Date(),
              comentario: rollback?.comentario ?? undefined,
            }))
          : [],

      // Plano de Escalonamento
      escalonamento:
        mountedData.escalonamento?.length > 0
          ? mountedData.escalonamento.map(escalonamento => ({
              idResponsavel: String(escalonamento.idResponsavel || ''),
              nome: escalonamento.responsavel?.nome || '',
              telefone: escalonamento.responsavel?.telefone1 || '',
            }))
          : [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'atividades',
  });
  const {
    fields: fieldsValidacao,
    append: appendValidacao,
    remove: removeValidacao,
  } = useFieldArray({
    control,
    name: 'validacao',
  });
  const {
    fields: fieldsRollback,
    append: appendRollback,
    remove: removeRollback,
  } = useFieldArray({
    control,
    name: 'rollback',
  });
  const {
    fields: fieldsEscalonamento,
    append: appendEscalonamento,
    remove: removeEscalonamento,
  } = useFieldArray({
    control,
    name: 'escalonamento',
  });
  const {
    fields: fieldsLinksTdnTotvs,
    append: appendLinksTdnTotvs,
    remove: removeLinksTdnTotvs,
  } = useFieldArray({
    control,
    name: 'linksTdnTotvs',
  });

  const dataSolicitacao = watch('dataSolicitacao');
  const dataHoraInicio = watch('dataHoraInicio');
  const indisponibilidadeValue = watch('indisponibilidade');
  const janelaAcordadaValue = watch('janelaAcordada');
  const dependeDeOutraMudancaValue = watch('dependeDeOutraMudanca');
  const outrosValue = watch('outros');
  const compilaFonteValue = watch('compilaFonte');
  const solicitanteValue = watch('idSolicitante');
  const origemValue = watch('origem');
  const ambienteValue = watch('ambiente');
  const statusValue = watch('status');
  const clienteValue = watch('idCliente');

  async function handleSubmit(data) {
    if (editShow) {
      ShowError('Não é possível editar uma GMUD sem estar no modo de edição.');
      return;
    }
    // if (editShow) {
    //   ShowError('Não é possível editar uma GMUD já aprovada ou em execução.');
    //   return;
    // }
    if (!mountedData) {
      ShowError('Não foi possível atualizar a GMUD, pois não há dados de GMUD carregados.');
      return;
    }
    try {
      const perfilResponse = await api.get('/signed/empresa/administracao/gerenciamento/perfil', {
        params: { id: perfil.idPerfil, idEmpresa: perfil.idEmpresa },
      });

      const body = {
        ...data,
        area: data.area,
        idCliente: Number(data.idCliente),
        idEmpresa: perfil.idEmpresa,
        idUnidade: perfil.idUnidade,
        idSolicitante: Number(data.idSolicitante),
        idCoordenador: Number(data.idCoordenador),
        idExecutor: Number(data.idExecutor),
        idModuloTotvs: Number(data.idModuloTotvs),
        idVersaoSistemaTotvs:
          data.ambiente === 'producao' ? Number(data.idVersaoSistemaTotvs) : null,
        idUsuarioCadastro: perfilResponse.data?.idUsuario,

        githubHashCommit: data?.compilaFonte ? data.githubHashCommit : null,
        outraClassificacao: data?.outros ? data.outrosClassificacao : null,

        tempoIndisponibilidadeEmMinutos:
          data?.indisponibilidade === 'sim' ? data.tempoIndisponibilidade : null,
        janelaAcordadaComQuem: data?.janelaAcordada === 'sim' ? data.janelaAcordadaComQuem : null,
        dependeDeOutraMudancaQual:
          data?.dependeDeOutraMudanca === 'sim' ? data.dependeDeOutraMudancaQual : null,

        atividades: data.atividades.map(atividadeItem => ({
          ...atividadeItem,
          idResponsavel: Number(atividadeItem.idResponsavel),
        })),
        validacao: data.validacao.map(validacaoItem => ({
          ...validacaoItem,
          passfail:
            validacaoItem?.passfail === 'pass' ? 1 : validacaoItem?.passfail === 'fail' ? 0 : null,
        })),
        rollback: data.rollback.map(rollbackItem => ({
          ...rollbackItem,
          idResponsavel: Number(rollbackItem.idResponsavel),
        })),
        escalonamento: data.escalonamento.map(escalonamento => ({
          ...escalonamento,
          idResponsavel: Number(escalonamento.idResponsavel),
        })),
      };

      return ShowConfirmacao(
        async () => {
          let response;
          try {
            response = await api.put(`/signed/empresa/gestorprojeto/gmud/${mountedData.id}`, body);
          } catch (error) {
            console.error('Erro ao atualizar GMUD:', error);
            Sentry.captureException(error);
          }

          refetch && refetch();

          if (body?.status === 'concluida') {
            const gmudData = response.data;
            const clientesSolicitanteData =
              clientesSolicitantes.find(c => c.id === Number(solicitanteValue)) || {};

            const bodyDataGmudDocument = {
              ...data,
              dataHoraInicio: formatDateToSaoPauloTimezone(data?.dataHoraInicio),
              dataHoraFim: formatDateToSaoPauloTimezone(data?.dataHoraFim),
              outraClassificacao: data?.outros ? data?.outrosClassificacao : '',
              clienteSolicitanteGmud: clientesSolicitanteData,
              tempoIndisponibilidadeEmMinutos:
                data?.indisponibilidade === 'sim' ? data.tempoIndisponibilidade : null,
              linksTdnTotvs: data?.linksTdnTotvs || [],
              execucao: data?.atividades?.map(execucaoItem => {
                const analista = analistas?.find(
                  a => a?.perfil?.usuario?.id === Number(execucaoItem.idResponsavel),
                );
                return {
                  responsavel: {
                    nome: analista?.nome ?? '',
                  },
                  descricao: execucaoItem?.descricao,
                  dataHoraInicio: formatDateToSaoPauloTimezone(execucaoItem?.dataHoraInicio),
                  tempoPrevistoEmMinutos: execucaoItem?.tempoPrevistoEmMinutos,
                };
              }),
              rollback: data.rollback.map(rollbackItem => {
                const analista = analistas?.find(
                  a => a?.perfil?.usuario?.id === Number(rollbackItem.idResponsavel),
                );
                return {
                  ...rollbackItem,
                  datahora: formatDateToSaoPauloTimezone(rollbackItem?.datahora),
                  responsavel: {
                    nome: analista?.nome ?? '',
                  },
                };
              }),
              escalonamento: data?.escalonamento?.map(escalonamentoItem => {
                const analista = analistas.find(
                  a => a?.perfil?.usuario?.id === Number(escalonamentoItem?.idResponsavel),
                );
                return {
                  responsavel: {
                    nome: analista?.nome ?? '',
                    telefone: analista?.telefone ?? '',
                    email: analista?.perfil?.usuario?.email ?? '',
                  },
                };
              }),
              idGmud: gmudData.id,
            };

            const pdfBlob = await pdf(
              <GmudPdfTemplatePDF key={Date.now()} {...bodyDataGmudDocument} />,
            ).toBlob();

            const reader = new FileReader();
            reader.readAsDataURL(pdfBlob);

            reader.onloadend = async () => {
              const base64data = reader.result.split(',')[1];

              await api.post('/signed/empresa/gestorprojeto/gmud/send-email', {
                gmudId: gmudData.id,
                pdfBase64: base64data,
                emailSubject: `GMUD #${gmudData.id} - ${gmudData.titulo}`,
                emailBody: `Uma GMUD foi editada: ${gmudData.titulo}`,
              });
            };
          }

          if (refetch) {
            refetch();
          }
          state.reloadData();
          dispatch(sistemaListShowModal(false));

          return response;
        },
        {
          errorModal: {
            defaultMessage: `Ocorreu um erro ao atualizar a sua GMUD. Por favor, tente novamente.`,
          },
          message: `Tem Certeza que deseja atualizar a GMUD?`,
          successModal: `GMUD atualizada com sucesso!`,
        },
      );
    } catch (error) {
      console.error('Erro ao atualizar GMUD:', error);

      const errorMessage = 'Ocorreu um erro ao atualizar a GMUD. Por favor, tente novamente.';
      ShowError(errorMessage);
      throw error;
    }
  }

  const clienteList = useMemo(
    () =>
      clientes?.map(client => ({
        value: String(client?.id ?? ''),
        label: client?.nomeFantasia ?? '',
      })),
    [clientes],
  );

  const clienteListSolicitante = useMemo(
    () =>
      clientesSolicitantes
        ?.filter(c => c.idCliente === Number(clienteValue))
        ?.map(solicitante => ({
          value: String(solicitante.id),
          label: solicitante.nome,
        })) || [],
    [clientesSolicitantes, clienteValue],
  );
  const analistaListFormatted = useMemo(
    () =>
      analistas.map(analista => ({
        value: String(analista?.perfil?.usuario?.id),
        label: analista?.perfil?.usuario?.nome,
      })),
    [analistas],
  );

  const modulosTotvsFormatted = useMemo(
    () =>
      modulosTotvs?.map(sistema => ({
        value: String(sistema.id),
        label: sistema.nome,
      })) || [],
    [modulosTotvs],
  );

  const versoesSistemaTotvsFormatted = useMemo(
    () =>
      versoesSistemaTotvs?.map(versao => ({
        value: String(versao.id),
        label: versao.nome,
      })) || [],
    [versoesSistemaTotvs],
  );

  return (
    <SLForm onSubmit={handleFormSubmit(handleSubmit)} defaultStyle>
      <S.BodyPageContainer>
        <FormSectionCard title="Dados do Solicitante">
          <S.CardBodyContainer>
            <Controller
              control={control}
              name="idCliente"
              render={({ field }) => (
                <InputSelect
                  label="Cliente"
                  options={[{ value: '', label: 'Selecione o cliente' }, ...clienteList]}
                  value={field.value}
                  onChange={field.onChange}
                  required
                  error={errors.idCliente?.message}
                  isDisabled={!!editShow}
                  isClearable={false}
                />
              )}
            />
            <Controller
              control={control}
              name="idSolicitante"
              render={({ field }) => (
                <InputSelect
                  label="Solicitante"
                  options={[
                    { value: '', label: 'Selecione o solicitante' },
                    ...clienteListSolicitante,
                  ]}
                  value={field.value}
                  onChange={field.onChange}
                  required
                  error={errors.idSolicitante?.message}
                  isDisabled={!!editShow}
                  isClearable={false}
                />
              )}
            />
            <Controller
              control={control}
              name="idModuloTotvs"
              render={({ field }) => (
                <InputSelect
                  label="Módulos *"
                  options={[
                    { value: '', label: 'Selecione o módulo TOTVS' },
                    ...modulosTotvsFormatted,
                  ]}
                  value={field.value}
                  onChange={field.onChange}
                  required
                  error={errors.idModuloTotvs?.message}
                  isDisabled={!!editShow}
                  isClearable={false}
                />
              )}
            />
            {/* <InputText
              label="Email"
              placeholder="Email do solicitante"
              {...register('solicitanteEmail')}
              error={errors?.solicitanteEmail?.message}
              readOnly
            /> */}
            {/* <InputText
              label="Área *"
              placeholder="Digite a área do solicitante"
              {...register('area')}
              error={errors.area?.message}
              required
              readOnly={!!editShow}
            /> */}
            <Controller
              control={control}
              name={`dataSolicitacao`}
              render={({ field }) => (
                <InputDate
                  name={field.name}
                  label="Data da solicitação *"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Data da solicitação"
                  error={errors?.dataSolicitacao?.message}
                  disabled={!!editShow}
                />
              )}
            />
          </S.CardBodyContainer>
        </FormSectionCard>

        {/* Vou adicionar o resto do formulário completo aqui */}
        <FormSectionCard title="Controle e Responsáveis da GMUD">
          <div>
            <S.ResponsaveisMudancaContainer>
              <Controller
                control={control}
                name="status"
                render={({ field }) => (
                  <InputSelect
                    label="Status *"
                    options={[
                      { value: 'pendente', label: 'Pendente' },
                      { value: 'concluida', label: 'Concluída' },
                      { value: 'cancelada', label: 'Cancelada' },
                    ]}
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.status?.message}
                    isDisabled={!!editShow}
                    isClearable={false}
                  />
                )}
              />
              <Controller
                control={control}
                name="idCoordenador"
                render={({ field }) => (
                  <InputSelect
                    label="Coordenador *"
                    options={coordenadores.map(coordenador => ({
                      value: String(coordenador?.id),
                      label: coordenador?.nome,
                    }))}
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.idCoordenador?.message}
                    isDisabled={!!editShow}
                    isClearable={false}
                  />
                )}
              />
              <Controller
                control={control}
                name="idExecutor"
                render={({ field }) => (
                  <InputSelect
                    label="Executor *"
                    options={analistaListFormatted}
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.idExecutor?.message}
                    isDisabled={!!editShow}
                    isClearable={false}
                  />
                )}
              />

              <Controller
                control={control}
                name={`dataHoraInicio`}
                render={({ field }) => (
                  <InputDateTime
                    name={field.name}
                    label="Data e Hora Início *"
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Data e Hora Início"
                    minDate={dataSolicitacao}
                    error={errors?.dataHoraInicio?.message}
                    disabled={!!editShow}
                  />
                )}
              />
              <Controller
                control={control}
                name={`dataHoraFim`}
                render={({ field }) => (
                  <InputDateTime
                    name={field.name}
                    label="Data e Hora Fim *"
                    value={field.value}
                    onChange={value => {
                      setError('dataHoraFim', {});

                      if (value && dataHoraInicio) {
                        const dataFim = new Date(value);
                        const dataInicio = new Date(dataHoraInicio);

                        if (dataFim <= dataInicio) {
                          setError('dataHoraFim', {
                            type: 'manual',
                            message:
                              'A data e hora de fim deve ser posterior à data e hora de início',
                          });

                          return;
                        }
                      }

                      field.onChange(value);
                    }}
                    placeholder="Data e Hora Fim"
                    minDate={dataHoraInicio}
                    disabled={!!editShow || !dataHoraInicio}
                    error={errors?.dataHoraFim?.message}
                  />
                )}
              />
            </S.ResponsaveisMudancaContainer>
          </div>
          {statusValue === 'cancelada' && (
            <div style={{ marginTop: 16 }}>
              <InputTextarea
                label="Motivo do Cancelamento *"
                placeholder="Digite o motivo do cancelamento"
                {...register('motivoCancelamento')}
                error={errors.motivoCancelamento?.message}
                required
                rows={2}
              />
            </div>
          )}
        </FormSectionCard>

        <FormSectionCard
          title="Descrição das Mudanças"
          subTitle="Detalhe o que será alterado e as atividades envolvidas."
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24, marginTop: 16 }}>
            <InputText
              label="Título *"
              placeholder="Digite o título da GMUD"
              {...register('titulo')}
              error={errors.titulo?.message}
              required
              readOnly={!!editShow}
            />
            <InputTextarea
              label="Descrição da Mudança *"
              placeholder="Digite a descrição da mudança"
              {...register('descricaoMudanca')}
              error={errors.descricaoMudanca?.message}
              required
              rows={2}
              readOnly={!!editShow}
            />
            <InputTextarea
              label="Descrição da Atividade *"
              placeholder="Digite a descrição da atividade"
              {...register('descricaoAtividade')}
              error={errors.descricaoAtividade?.message}
              required
              readOnly={!!editShow}
            />
          </div>

          <S.Line />

          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
            <S.HeaderWrapper>
              <S.TitleWrapper>
                <S.CardTitle>Links TDN TOTVS</S.CardTitle>
                <S.CardSubTitle
                  style={{
                    marginTop: -4,
                  }}
                >
                  Adicione links relevantes da documentação TDN TOTVS
                </S.CardSubTitle>
              </S.TitleWrapper>

              <S.AddButton type="button" onClick={() => appendLinksTdnTotvs({ url: '' })}>
                <i
                  className="material-icons"
                  style={{
                    fontSize: '28px',
                    color: localStorage.getItem('tema') == 'E' ? '#ffffff' : '#6796cf',
                  }}
                >
                  add
                </i>
              </S.AddButton>
            </S.HeaderWrapper>

            {fieldsLinksTdnTotvs.map((field, index) => (
              <div key={field.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ position: 'relative', flex: 1 }}>
                  <InputText
                    placeholder="https://suporte.totvs.com/..."
                    {...register(`linksTdnTotvs.${index}.url`)}
                    error={errors.linksTdnTotvs?.[index]?.url?.message}
                    style={{ paddingRight: '40px' }}
                  />
                </div>
                <S.DeleteButton
                  title="Remover"
                  type="button"
                  onClick={() => removeLinksTdnTotvs(index)}
                >
                  <img src={IconLixeira} alt="lixeira" />
                </S.DeleteButton>
              </div>
            ))}
          </div>
        </FormSectionCard>

        {/* Classificações */}
        <FormSectionCard
          title="Classificações"
          subTitle="Selecione as classificações adequadas para a GMUD"
          isLine
        >
          <S.ClassificacoesContainer>
            <InputCheckbox
              label="Atualização de Patch"
              {...register('atualizacaoDePatch')}
              error={errors.atualizacaoDePatch?.message}
              disabled={!!editShow}
              style={editShow ? { opacity: 1, filter: 'none' } : {}}
            />
            <InputCheckbox
              label="Atualização de LIB"
              {...register('atualizacaoDeLib')}
              error={errors.atualizacaoDeLib?.message}
              disabled={!!editShow}
              style={editShow ? { opacity: 1, filter: 'none' } : {}}
            />
            <InputCheckbox
              label="Atualização de Stored Procedure"
              {...register('atualizacaoDeStoredProcedure')}
              error={errors.atualizacaoDeStoredProcedure?.message}
              disabled={!!editShow}
              style={editShow ? { opacity: 1, filter: 'none' } : {}}
            />
            <InputCheckbox
              label="Atualização de Schemas TSS"
              {...register('atualizacaoDeSchemasTss')}
              error={errors.atualizacaoDeSchemasTss?.message}
              disabled={!!editShow}
              style={editShow ? { opacity: 1, filter: 'none' } : {}}
            />
            <InputCheckbox
              label="Atualização de Mashup"
              {...register('atualizacaoDeMashup')}
              error={errors.atualizacaoDeMashup?.message}
              disabled={!!editShow}
              style={editShow ? { opacity: 1, filter: 'none' } : {}}
            />
            <InputCheckbox
              label="Atualização de TSS"
              {...register('atualizacaoDeTss')}
              error={errors.atualizacaoDeTss?.message}
              disabled={!!editShow}
              style={editShow ? { opacity: 1, filter: 'none' } : {}}
            />
            <InputCheckbox
              label="Atualização de UPDDISTR"
              {...register('atualizacaoDeUpddistr')}
              error={errors.atualizacaoDeUpddistr?.message}
              disabled={!!editShow}
              style={editShow ? { opacity: 1, filter: 'none' } : {}}
            />
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <InputCheckbox
                label="Compila Fonte"
                {...register('compilaFonte')}
                error={errors.compilaFonte?.message}
                disabled={!!editShow}
                style={editShow ? { opacity: 1, filter: 'none' } : {}}
              />
              {compilaFonteValue && (
                <InputText
                  placeholder="Ex: ffc3ad03eb1496f8a489b9c4873965e1a1540393"
                  {...register('githubHashCommit')}
                  error={errors.githubHashCommit?.message}
                  required
                  readOnly={!!editShow}
                  styleContainer={{
                    paddingRight: '24px',
                    width: '60%',
                  }}
                />
              )}
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <InputCheckbox
                label={`Outros ${outrosValue ? ':' : ''}`}
                {...register('outros')}
                error={errors.outros?.message}
                disabled={!!editShow}
                style={editShow ? { opacity: 1, filter: 'none' } : {}}
              />
              {outrosValue && (
                <InputText
                  placeholder="Digite o outro tipo de classificação"
                  {...register('outrosClassificacao')}
                  error={errors.outrosClassificacao?.message}
                  required
                  readOnly={!!editShow}
                  styleContainer={{
                    paddingRight: '36px',
                    width: '70%',
                  }}
                />
              )}
            </div>
          </S.ClassificacoesContainer>
        </FormSectionCard>

        {/* Radios Container */}
        <S.RadiosContainer>
          <FormSectionCard title="Categoria" isLine>
            <S.RadioContent>
              <InputRadio
                label="Agendada"
                value="agendada"
                {...register('categoria')}
                error={errors.categoria?.message}
                disabled={!!editShow}
                style={editShow ? { opacity: 1, filter: 'none' } : {}}
              />
              <InputRadio
                label="Emergencial"
                value="emergencial"
                {...register('categoria')}
                error={errors.categoria?.message}
                disabled={!!editShow}
                style={editShow ? { opacity: 1, filter: 'none' } : {}}
              />
              <InputRadio
                label="Padrão"
                value="padrao"
                {...register('categoria')}
                error={errors.categoria?.message}
                disabled={!!editShow}
                style={editShow ? { opacity: 1, filter: 'none' } : {}}
              />
            </S.RadioContent>
          </FormSectionCard>

          <FormSectionCard title="Prioridade" isLine>
            <S.RadioContent>
              <InputRadio
                label="Baixa"
                value="baixa"
                {...register('prioridade')}
                error={errors.prioridade?.message}
                disabled={!!editShow}
                // style={editShow ? { opacity: 1, filter: 'none' } : {}}
              />
              <InputRadio
                label="Média"
                value="media"
                {...register('prioridade')}
                error={errors.prioridade?.message}
                disabled={!!editShow}
                // style={editShow ? { opacity: 1, filter: 'none' } : {}}
              />
              <InputRadio
                label="Alta"
                value="alta"
                {...register('prioridade')}
                error={errors.prioridade?.message}
                disabled={!!editShow}
                // style={editShow ? { opacity: 1, filter: 'none' } : {}}
              />
            </S.RadioContent>
          </FormSectionCard>

          <FormSectionCard title="Origem" isLine>
            <S.RadioContent
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px',
              }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                <InputRadio
                  label="Projeto"
                  value="projeto"
                  {...register('origem')}
                  error={errors.origem?.message}
                  disabled={!!editShow}
                  style={editShow ? { opacity: 1, filter: 'none' } : {}}
                />
                <InputRadio
                  label="Incidente"
                  value="incidente"
                  {...register('origem')}
                  error={errors.origem?.message}
                  disabled={!!editShow}
                  style={editShow ? { opacity: 1, filter: 'none' } : {}}
                />
              </div>
              <InputRadio
                label="Produto"
                value="produto"
                {...register('origem')}
                error={errors.origem?.message}
                disabled={!!editShow}
                style={editShow ? { opacity: 1, filter: 'none' } : {}}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <InputRadio
                  label="Ticket"
                  value="ticket"
                  {...register('origem')}
                  error={errors.origem?.message}
                  disabled={!!editShow}
                  style={editShow ? { opacity: 1, filter: 'none' } : {}}
                />
                {origemValue === 'ticket' && (
                  <InputText
                    placeholder="Digite o número do ticket"
                    {...register('numeroTicket')}
                    error={errors.numeroTicket?.message}
                    readOnly={!!editShow}
                    styleContainer={{
                      marginTop: '-8px',
                      height: '24px',
                    }}
                    style={{
                      height: '24px',
                    }}
                  />
                )}
              </div>
            </S.RadioContent>
          </FormSectionCard>

          <FormSectionCard title="Ambiente" isLine>
            <S.RadioContent twoColumns>
              <InputRadio
                label="Produção"
                value="producao"
                {...register('ambiente')}
                error={errors.ambiente?.message}
                disabled={!!editShow}
                style={editShow ? { opacity: 1, filter: 'none' } : {}}
              />
              <InputRadio
                label="Desenvolvimento"
                value="desenvolvimento"
                {...register('ambiente')}
                error={errors.ambiente?.message}
                disabled={!!editShow}
                style={editShow ? { opacity: 1, filter: 'none' } : {}}
              />
            </S.RadioContent>

            <div>
              <Controller
                control={control}
                name="idVersaoSistemaTotvs"
                render={({ field }) => (
                  <InputSelect
                    label="Versão do Sistema TOTVS"
                    options={[
                      { value: '', label: 'Selecione a versão' },
                      ...versoesSistemaTotvsFormatted,
                    ]}
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.idVersaoSistemaTotvs?.message}
                    isDisabled={ambienteValue !== 'producao' || !!editShow}
                  />
                )}
              />
            </div>
          </FormSectionCard>
        </S.RadiosContainer>

        {/* Segunda linha de radios */}
        <S.RadiosContainer>
          <FormSectionCard title="Finalidade" isLine>
            <S.RadioContent twoColumns>
              <InputRadio
                label="Melhoria"
                value="melhoria"
                {...register('finalidade')}
                error={errors.finalidade?.message}
                disabled={!!editShow}
                style={editShow ? { opacity: 1, filter: 'none' } : {}}
              />
              <InputRadio
                label="Corretiva"
                value="corretiva"
                {...register('finalidade')}
                error={errors.finalidade?.message}
                disabled={!!editShow}
                style={editShow ? { opacity: 1, filter: 'none' } : {}}
              />
              <InputRadio
                label="Projeto"
                value="projeto"
                {...register('finalidade')}
                error={errors.finalidade?.message}
                disabled={!!editShow}
                style={editShow ? { opacity: 1, filter: 'none' } : {}}
              />
              <InputRadio
                label="Upgrade"
                value="upgrade"
                {...register('finalidade')}
                error={errors.finalidade?.message}
                disabled={!!editShow}
                style={editShow ? { opacity: 1, filter: 'none' } : {}}
              />
              <InputRadio
                label="teste"
                value="teste"
                {...register('finalidade')}
                error={errors.finalidade?.message}
                disabled={!!editShow}
                style={editShow ? { opacity: 1, filter: 'none' } : {}}
              />
              <InputRadio
                label="Preventiva"
                value="preventiva"
                {...register('finalidade')}
                error={errors.finalidade?.message}
                disabled={!!editShow}
                style={editShow ? { opacity: 1, filter: 'none' } : {}}
              />
            </S.RadioContent>
          </FormSectionCard>

          <FormSectionCard title="Haverá Indisponibilidade?" isLine>
            <S.RadioContent>
              <InputRadio
                label="Não"
                value="nao"
                {...register('indisponibilidade')}
                error={errors.indisponibilidade?.message}
                disabled={!!editShow}
                style={editShow ? { opacity: 1, filter: 'none' } : {}}
              />
              <InputRadio
                label="Sim"
                value="sim"
                {...register('indisponibilidade')}
                error={errors.indisponibilidade?.message}
                disabled={!!editShow}
                style={editShow ? { opacity: 1, filter: 'none' } : {}}
              />
              <Controller
                control={control}
                name={`tempoIndisponibilidade`}
                render={({ field }) => (
                  <InputTime
                    name={field.name}
                    label={`Tempo de Indisponibilidade ${
                      indisponibilidadeValue === 'sim' ? '*' : ''
                    }`}
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="HH:MM"
                    disabled={!!editShow || indisponibilidadeValue !== 'sim'}
                    error={errors.tempoIndisponibilidade?.message}
                    styleContainer={{ marginTop: '8px' }}
                  />
                )}
              />
            </S.RadioContent>
          </FormSectionCard>

          <FormSectionCard title="Janela Acordada" isLine>
            <S.RadioContent>
              <InputRadio
                label="Não"
                value="nao"
                {...register('janelaAcordada')}
                error={errors.janelaAcordada?.message}
                disabled={!!editShow}
                style={editShow ? { opacity: 1, filter: 'none' } : {}}
              />
              <InputRadio
                label="Sim"
                value="sim"
                {...register('janelaAcordada')}
                error={errors.janelaAcordada?.message}
                disabled={!!editShow}
                style={editShow ? { opacity: 1, filter: 'none' } : {}}
              />
              <InputText
                label={`Com Quem? ${janelaAcordadaValue === 'sim' ? '*' : ''}`}
                placeholder="Digite com quem a janela foi acordada"
                {...register('janelaAcordadaComQuem')}
                error={errors.janelaAcordadaComQuem?.message}
                disabled={janelaAcordadaValue !== 'sim'}
                readOnly={!!editShow}
                styleContainer={{
                  marginTop: '8px',
                }}
              />
            </S.RadioContent>
          </FormSectionCard>

          <FormSectionCard title="Depende de outra mudança?" isLine>
            <S.RadioContent>
              <InputRadio
                label="Não"
                value="nao"
                {...register('dependeDeOutraMudanca')}
                error={errors.dependeDeOutraMudanca?.message}
                disabled={!!editShow}
                style={editShow ? { opacity: 1, filter: 'none' } : {}}
              />
              <InputRadio
                label="Sim"
                value="sim"
                {...register('dependeDeOutraMudanca')}
                error={errors.dependeDeOutraMudanca?.message}
                disabled={!!editShow}
                style={editShow ? { opacity: 1, filter: 'none' } : {}}
              />

              <InputText
                label={`Qual? ${dependeDeOutraMudancaValue === 'sim' ? '*' : ''}`}
                placeholder="Informe de qual mudança depende"
                {...register('dependeDeOutraMudancaQual')}
                error={errors.dependeDeOutraMudancaQual?.message}
                disabled={dependeDeOutraMudancaValue !== 'sim'}
                readOnly={!!editShow}
                styleContainer={{
                  marginTop: '8px',
                }}
              />
            </S.RadioContent>
          </FormSectionCard>
        </S.RadiosContainer>

        {/* Plano de Execução */}
        <FormSectionCard
          title="Plano de Execução da Mudança"
          onClickButton={
            !editShow
              ? () =>
                  append({
                    descricao: '',
                    idResponsavel: String(perfilAtual?.perfil?.usuario?.id || ''),
                    dataHoraInicio: null,
                    tempoPrevistoEmMinutos: 0,
                  })
              : undefined
          }
        >
          {fields.map((field, index) => (
            <S.CardPlanoContainer key={field.id}>
              <S.CardPlanoTitleContent>
                <p>Atividade - {index + 1}</p>
                <S.DeleteButton
                  title="Remover"
                  type="button"
                  onClick={() => fields.length > 1 && remove(index)}
                  disabled={!!editShow}
                  style={{
                    opacity: editShow ? 0.5 : 1,
                    cursor: editShow ? 'not-allowed' : 'pointer',
                  }}
                >
                  <img src={IconLixeira} alt="lixeira" />
                </S.DeleteButton>
              </S.CardPlanoTitleContent>
              <S.CardPlanoContent
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 0.4fr 0.3fr 0.3fr',
                  width: '100%',
                  gap: '16px',
                }}
              >
                <InputText
                  label="Atividade *"
                  placeholder="Digite a descrição da atividade"
                  {...register(`atividades.${index}.descricao`)}
                  error={errors.atividades?.[index]?.descricao?.message}
                  required
                  readOnly={!!editShow}
                />
                <Controller
                  control={control}
                  name={`atividades.${index}.idResponsavel`}
                  render={({ field }) => (
                    <InputSelect
                      label="Responsável *"
                      options={analistaListFormatted}
                      value={field.value}
                      onChange={field.onChange}
                      error={errors.atividades?.[index]?.idResponsavel?.message}
                      isDisabled={!!editShow}
                      isClearable={false}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name={`atividades.${index}.dataHoraInicio`}
                  render={({ field }) => (
                    <InputDateTime
                      name={field.name}
                      label="Data e Hora Início *"
                      value={field.value}
                      onChange={value => {
                        setError(`atividades.${index}.dataHoraInicio`, {});

                        if (value && dataHoraInicio) {
                          const dataFim = new Date(value);
                          const dataInicio = new Date(dataHoraInicio);

                          if (dataFim <= dataInicio) {
                            setError(`atividades.${index}.dataHoraInicio`, {
                              type: 'manual',
                              message:
                                'A data e hora da atividade deve ser posterior à data e hora de início da GMUD',
                            });

                            return;
                          }
                        }

                        field.onChange(value);
                      }}
                      placeholder="Data e Hora Início"
                      minDate={dataHoraInicio}
                      disabled={!!editShow || !dataHoraInicio}
                      error={errors.atividades?.[index]?.dataHoraInicio?.message}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name={`atividades.${index}.tempoPrevistoEmMinutos`}
                  render={({ field }) => (
                    <InputTime
                      name={field.name}
                      label="Tempo Previsto *"
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="HH:MM"
                      disabled={!!editShow || !dataHoraInicio}
                      error={errors.atividades?.[index]?.tempoPrevistoEmMinutos?.message}
                    />
                  )}
                />
              </S.CardPlanoContent>
            </S.CardPlanoContainer>
          ))}
        </FormSectionCard>

        {/* Plano de Validação */}
        <FormSectionCard
          title="Plano de Validação da Mudança"
          onClickButton={
            !editShow
              ? () =>
                  appendValidacao({
                    descricao: '',
                    resultado: '',
                    passfail: '',
                    comentario: '',
                  })
              : undefined
          }
        >
          {fieldsValidacao.map((field, index) => (
            <S.CardPlanoContainer key={field.id}>
              <S.CardPlanoTitleContent>
                <p>Ação - {index + 1}</p>
                <S.DeleteButton
                  title="Remover"
                  type="button"
                  onClick={() => fieldsValidacao.length > 1 && removeValidacao(index)}
                  disabled={!!editShow}
                  style={{
                    opacity: editShow ? 0.5 : 1,
                    cursor: editShow ? 'not-allowed' : 'pointer',
                  }}
                >
                  <img src={IconLixeira} alt="lixeira" />
                </S.DeleteButton>
              </S.CardPlanoTitleContent>
              <S.CardPlanoContent style={{ flexDirection: 'column' }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '0.3fr 1fr',
                    width: '100%',
                    gap: '16px',
                  }}
                >
                  <Controller
                    control={control}
                    name={`validacao.${index}.passfail`}
                    render={({ field }) => (
                      <InputSelect
                        label="Passou / Falhou"
                        options={[
                          { value: '', label: 'Selecione' },
                          { value: 'pass', label: 'Passou' },
                          { value: 'fail', label: 'Falhou' },
                        ]}
                        value={field.value}
                        onChange={field.onChange}
                        error={errors.validacao?.[index]?.passfail?.message}
                        isDisabled={!!editShow}
                        isClearable={false}
                      />
                    )}
                  />
                  <InputText
                    label="Descrição da Ação *"
                    placeholder="Digite a descrição"
                    {...register(`validacao.${index}.descricao`)}
                    error={errors.validacao?.[index]?.descricao?.message}
                    required
                    readOnly={!!editShow}
                  />
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '0.3fr 1fr',
                    width: '100%',
                    gap: '16px',
                  }}
                >
                  <InputText
                    label="Comentário"
                    placeholder="Comentário"
                    {...register(`validacao.${index}.comentario`)}
                    error={errors.validacao?.[index]?.comentario?.message}
                    style={{ width: '100%' }}
                    readOnly={!!editShow}
                  />
                  <InputText
                    label="Resultado Esperado *"
                    placeholder="Digite o resultado esperado"
                    {...register(`validacao.${index}.resultado`)}
                    error={errors.validacao?.[index]?.resultado?.message}
                    required
                    readOnly={!!editShow}
                  />
                </div>
              </S.CardPlanoContent>
            </S.CardPlanoContainer>
          ))}
        </FormSectionCard>

        {/* Plano de Rollback */}
        <FormSectionCard
          title="Plano de Rollback (para o caso de problemas)"
          onClickButton={
            !editShow
              ? () =>
                  appendRollback({
                    descricao: '',
                    idResponsavel: String(perfilAtual?.perfil?.usuario?.id || ''),
                    datahora: null,
                    comentario: '',
                  })
              : undefined
          }
        >
          {fieldsRollback.map((field, index) => (
            <S.CardPlanoContainer key={field.id}>
              <S.CardPlanoTitleContent>
                <p>Ação - {index + 1}</p>
                <S.DeleteButton
                  title="Remover"
                  type="button"
                  onClick={() => fieldsRollback.length > 1 && removeRollback(index)}
                  disabled={!!editShow}
                  style={{
                    opacity: editShow ? 0.5 : 1,
                    cursor: editShow ? 'not-allowed' : 'pointer',
                  }}
                >
                  <img src={IconLixeira} alt="lixeira" />
                </S.DeleteButton>
              </S.CardPlanoTitleContent>
              <S.CardPlanoContent style={{ flexDirection: 'column' }}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '0.3fr 1fr',
                    width: '100%',
                    gap: '16px',
                  }}
                >
                  <Controller
                    control={control}
                    name={`rollback.${index}.datahora`}
                    render={({ field }) => (
                      <InputDateTime
                        name={field.name}
                        label="Data/Hora *"
                        value={field.value}
                        onChange={value => {
                          setError(`rollback.${index}.datahora`, {});
                          if (value && dataHoraInicio) {
                            const dataFim = new Date(value);
                            const dataInicio = new Date(dataHoraInicio);

                            if (dataFim <= dataInicio) {
                              setError(`rollback.${index}.datahora`, {
                                type: 'manual',
                                message:
                                  'A data e hora do rollback deve ser posterior à data e hora de início da GMUD',
                              });

                              return;
                            }
                          }

                          field.onChange(value);
                        }}
                        placeholder="Data e hora"
                        minDate={dataHoraInicio}
                        disabled={!!editShow || !dataHoraInicio}
                        error={errors.rollback?.[index]?.datahora?.message}
                      />
                    )}
                  />
                  <InputText
                    label="Descrição da Ação *"
                    placeholder="Digite a descrição"
                    {...register(`rollback.${index}.descricao`)}
                    error={errors.rollback?.[index]?.descricao?.message}
                    required
                    readOnly={!!editShow}
                  />
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '0.3fr 1fr',
                    width: '100%',
                    gap: '16px',
                  }}
                >
                  <Controller
                    control={control}
                    name={`rollback.${index}.idResponsavel`}
                    render={({ field }) => (
                      <InputSelect
                        label="Responsável *"
                        options={analistaListFormatted}
                        value={field.value}
                        onChange={e => field.onChange(e)}
                        error={errors.rollback?.[index]?.idResponsavel?.message}
                        isDisabled={!!editShow}
                        isClearable={false}
                      />
                    )}
                  />
                  <InputText
                    label="Comentário"
                    placeholder="Comentário"
                    {...register(`rollback.${index}.comentario`)}
                    error={errors.rollback?.[index]?.comentario?.message}
                    readOnly={!!editShow}
                  />
                </div>
              </S.CardPlanoContent>
            </S.CardPlanoContainer>
          ))}
        </FormSectionCard>

        {/* Plano de Escalonamento */}
        <FormSectionCard
          title="Plano de Escalonamento em Caso de Problemas"
          onClickButton={
            !editShow
              ? () => appendEscalonamento({ idResponsavel: '', nome: '', telefone: '' })
              : undefined
          }
        >
          {fieldsEscalonamento.map((field, index) => (
            <S.CardPlanoContainer key={field.id}>
              <S.CardPlanoTitleContent>
                <p>Contato - {index + 1}</p>

                <S.DeleteButton
                  title="Remover"
                  type="button"
                  onClick={() => fieldsEscalonamento.length > 1 && removeEscalonamento(index)}
                  disabled={!!editShow}
                  style={{
                    opacity: editShow ? 0.5 : 1,
                    cursor: editShow ? 'not-allowed' : 'pointer',
                  }}
                >
                  <img src={IconLixeira} alt="lixeira" />
                </S.DeleteButton>
              </S.CardPlanoTitleContent>
              <S.CardPlanoContent>
                <Controller
                  control={control}
                  name={`escalonamento.${index}.idResponsavel`}
                  render={({ field }) => (
                    <InputSelect
                      label="Responsável *"
                      options={analistaListFormatted}
                      value={field.value}
                      onChange={e => {
                        field.onChange(e);
                        const selectedValue = e.target.value;

                        const selectedAnalista = analistas.find(
                          a => a.perfil?.usuario?.id === Number(selectedValue),
                        );
                        if (selectedAnalista) {
                          setValue(`escalonamento.${index}.nome`, selectedAnalista.nome || '');
                          setValue(
                            `escalonamento.${index}.telefone`,
                            selectedAnalista.perfil?.usuario?.telefone1 || '',
                          );
                        }
                      }}
                      error={errors.escalonamento?.[index]?.idResponsavel?.message}
                      isDisabled={!!editShow}
                      isClearable={false}
                    />
                  )}
                />
                <InputText
                  label="Nome *"
                  placeholder="Digite o nome"
                  {...register(`escalonamento.${index}.nome`)}
                  error={errors.escalonamento?.[index]?.nome?.message}
                  required
                  readOnly
                />
                <InputText
                  label="Telefone"
                  placeholder="Digite o telefone"
                  {...register(`escalonamento.${index}.telefone`)}
                  error={errors.escalonamento?.[index]?.telefone?.message}
                  required
                  readOnly
                />
              </S.CardPlanoContent>
            </S.CardPlanoContainer>
          ))}
        </FormSectionCard>
      </S.BodyPageContainer>
    </SLForm>
  );
}

export default EditContent;
